import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import type { QuerySnapshot } from "firebase/firestore";
import { getFirebaseFirestore } from "@/config/firebase";
import { isDemoMode } from "@/demo/demoMode";
import * as demoStore from "@/demo/demoStore";
import type {
  ClassType,
  EquipmentType,
  ExerciseEntry,
  Journal,
  JournalMovementEntry,
} from "@vlab-corporation/vlab-pilates-service-interface/class";
import type { ClassFormat } from "@vlab-corporation/vlab-pilates-service-interface/branch";
import type { ID } from "@vlab-corporation/vlab-pilates-service-interface/types";

/** createdAt은 serverTimestamp()로 저장되므로 런타임에 Firestore Timestamp 객체임. 밀리초로 변환. */
function toTimestampMs(createdAt: unknown): number {
  if (typeof createdAt === "string") return new Date(createdAt).getTime() || 0;
  if (createdAt && typeof createdAt === "object") {
    if ("toDate" in createdAt && typeof (createdAt as { toDate: unknown }).toDate === "function") {
      return (createdAt as { toDate(): Date }).toDate().getTime();
    }
    if ("seconds" in createdAt && typeof (createdAt as { seconds: unknown }).seconds === "number") {
      return (createdAt as { seconds: number }).seconds * 1000;
    }
  }
  return 0;
}

export interface JournalDocument {
  classId: ID;
  instructorId: ID;
  /** @deprecated studiomateMemberId를 사용하세요. 하위 호환용으로만 유지됩니다. */
  memberId?: string;
  /** Studiomate 회원 고유 ID (숫자). getLecture API에서 보장됨. */
  studiomateMemberId?: number;
  /** 수업 유형: PRIVATE_REGULAR | GROUP_REGULAR */
  classType?: ClassType;
  /** OT 수업 여부 */
  isOt?: boolean;
  /** @deprecated classType + isOt를 사용하세요. Firestore 기존 데이터 호환용. */
  classFormat?: ClassFormat;
  equipments: EquipmentType[];
  exercises: ExerciseEntry[];
  problemList: string;
  improvements?: string;
  improvementsNeeded?: string;
  notes?: string;
  bodyAnalysis?: string;
  memberGoal?: string;
  instructorMemo?: string;
  createdAt: unknown;
  updatedAt: unknown;
  // 구버전 호환 (Firestore 기존 데이터)
  movements?: JournalMovementEntry[];
}

type UpdateableJournalFields = Partial<
  Pick<
    JournalDocument,
    | "classType"
    | "isOt"
    | "classFormat"
    | "equipments"
    | "exercises"
    | "problemList"
    | "improvements"
    | "improvementsNeeded"
    | "notes"
    | "bodyAnalysis"
    | "memberGoal"
    | "instructorMemo"
    | "memberId"
    | "studiomateMemberId"
  >
>;

function journalsCollection(ownerUid: string, centerId: number) {
  return collection(
    getFirebaseFirestore(),
    "users",
    ownerUid,
    "centers",
    String(centerId),
    "journals",
  );
}

function draftJournalsCollection(ownerUid: string, centerId: number) {
  return collection(
    getFirebaseFirestore(),
    "users",
    ownerUid,
    "centers",
    String(centerId),
    "draftJournals",
  );
}

export async function getJournalByClassId(
  ownerUid: string,
  centerId: number,
  classId: ID,
  instructorId?: ID,
): Promise<(JournalDocument & { id: string }) | null> {
  if (isDemoMode()) return demoStore.getJournalByClassId(centerId, classId, instructorId);
  const q = query(journalsCollection(ownerUid, centerId), where("classId", "==", classId));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const docSnap = snap.docs[0];
  const data = docSnap.data() as JournalDocument;
  // 강사 필터: 다른 강사의 일지는 조회하지 않음
  if (instructorId && data.instructorId !== instructorId) return null;
  return { id: docSnap.id, ...data };
}

export async function saveJournal(
  ownerUid: string,
  centerId: number,
  data: Omit<JournalDocument, "createdAt" | "updatedAt">,
): Promise<string> {
  if (isDemoMode()) return demoStore.saveJournal(centerId, data);
  if (!data.exercises || data.exercises.length === 0) {
    throw new Error("Journal must have at least one exercise");
  }
  const ref = doc(journalsCollection(ownerUid, centerId));
  await setDoc(ref, {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateJournal(
  ownerUid: string,
  centerId: number,
  journalId: string,
  data: UpdateableJournalFields,
): Promise<void> {
  if (isDemoMode()) return demoStore.updateJournal(centerId, journalId, data);
  if (data.exercises && data.exercises.length === 0) {
    throw new Error("Journal must have at least one exercise");
  }
  const ref = doc(journalsCollection(ownerUid, centerId), journalId);
  await updateDoc(ref, {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

/**
 * 현재 일지 저장 + 기존 OT 일지 강등(isOt=false)을 하나의 writeBatch로 원자 처리한다.
 * 강사-회원당 OT 1개 불변식을 위해, OT를 켜서 저장할 때 기존 OT 일지를 정규로 되돌린다.
 * demoteJournalIds가 비면 강등 없이 현재 일지만 저장한다.
 */
export async function saveJournalWithOtDemotion(
  ownerUid: string,
  centerId: number,
  params: {
    existingJournalId: string | null;
    data: Omit<JournalDocument, "createdAt" | "updatedAt">;
    demoteJournalIds: string[];
  },
): Promise<string> {
  const { existingJournalId, data, demoteJournalIds } = params;
  if (!data.exercises || data.exercises.length === 0) {
    throw new Error("Journal must have at least one exercise");
  }

  // 데모 모드: 원자성 없이 순차 처리(데모 환경엔 실 정산이 없어 무해).
  if (isDemoMode()) {
    const id = existingJournalId
      ? (await updateJournal(ownerUid, centerId, existingJournalId, data), existingJournalId)
      : await saveJournal(ownerUid, centerId, data);
    for (const did of demoteJournalIds) {
      await updateJournal(ownerUid, centerId, did, { isOt: false });
    }
    return id;
  }

  const col = journalsCollection(ownerUid, centerId);
  const batch = writeBatch(getFirebaseFirestore());

  let journalId: string;
  if (existingJournalId) {
    batch.update(doc(col, existingJournalId), { ...data, updatedAt: serverTimestamp() });
    journalId = existingJournalId;
  } else {
    const ref = doc(col);
    batch.set(ref, { ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    journalId = ref.id;
  }

  for (const did of demoteJournalIds) {
    batch.update(doc(col, did), { isOt: false, updatedAt: serverTimestamp() });
  }

  await batch.commit();
  return journalId;
}

export async function saveDraftJournal(
  ownerUid: string,
  centerId: number,
  data: Omit<JournalDocument, "createdAt" | "updatedAt">,
): Promise<string> {
  if (isDemoMode()) return demoStore.saveJournal(centerId, data);
  const ref = doc(journalsCollection(ownerUid, centerId));
  await setDoc(ref, {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateDraftJournal(
  ownerUid: string,
  centerId: number,
  journalId: string,
  data: UpdateableJournalFields,
): Promise<void> {
  if (isDemoMode()) return demoStore.updateJournal(centerId, journalId, data);
  const ref = doc(journalsCollection(ownerUid, centerId), journalId);
  await updateDoc(ref, {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function getJournalById(
  ownerUid: string,
  centerId: number,
  journalId: string,
): Promise<(JournalDocument & { id: string }) | null> {
  if (isDemoMode()) return demoStore.getJournalById(centerId, journalId);
  const ref = doc(journalsCollection(ownerUid, centerId), journalId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as JournalDocument) };
}

/**
 * classId로 해당 수업의 모든 일지 목록을 반환한다.
 * 그룹 수업: N개, 개인 수업: 최대 1개
 */
export async function getJournalsByClassId(
  ownerUid: string,
  centerId: number,
  classId: string,
): Promise<Journal[]> {
  const q = query(journalsCollection(ownerUid, centerId), where("classId", "==", classId));
  const snap = await getDocs(q);
  return snap.docs.map((docSnap) => ({
    id: docSnap.id,
    ...(docSnap.data() as Omit<Journal, "id">),
  }));
}

/**
 * 특정 회원의 모든 일지를 작성일 역순으로 반환한다.
 * 작성자(instructorId) 무관하게 전체 조회.
 *
 * 3가지 방식으로 병합 검색:
 * 1. studiomateMemberId (number) — 새 버전 일지
 * 2. memberId == String(studiomateMemberId) — 숫자 문자열로 저장된 구버전 일지
 * 3. memberId == encodeURIComponent(memberName) — 이름으로 저장된 구버전 일지
 *
 * 중복 제거 후 createdAt 역순 정렬.
 */
export async function getJournalsByMemberId(
  ownerUid: string,
  centerId: number,
  studiomateMemberId: number,
  memberName?: string,
): Promise<Journal[]> {
  if (isDemoMode()) {
    return demoStore.getJournalsByMemberId(centerId, studiomateMemberId) as unknown as Journal[];
  }

  const col = journalsCollection(ownerUid, centerId);
  const memberIdStr = String(studiomateMemberId);

  // 병렬 쿼리 목록
  const queries: Array<Promise<QuerySnapshot>> = [
    getDocs(query(col, where("studiomateMemberId", "==", studiomateMemberId))),
    getDocs(query(col, where("memberId", "==", memberIdStr))),
  ];

  // 이름 기반 검색 (구버전: memberId = encodeURIComponent(memberName))
  if (memberName) {
    const encodedName = encodeURIComponent(memberName);
    if (encodedName !== memberIdStr) {
      queries.push(getDocs(query(col, where("memberId", "==", encodedName))));
    }
  }

  const snapshots = await Promise.all(queries);

  // 중복 제거
  const journalMap = new Map<string, Journal>();
  for (const snap of snapshots) {
    for (const docSnap of snap.docs) {
      if (!journalMap.has(docSnap.id)) {
        journalMap.set(docSnap.id, { id: docSnap.id, ...(docSnap.data() as Omit<Journal, "id">) });
      }
    }
  }

  const journals = [...journalMap.values()];
  // 클라이언트 정렬: createdAt 역순
  // createdAt은 serverTimestamp()로 저장되므로 런타임에 Firestore Timestamp 객체임
  journals.sort((a, b) => toTimestampMs(b.createdAt) - toTimestampMs(a.createdAt));
  return journals;
}

/**
 * classId + studiomateMemberId 조합으로 특정 회원의 일지를 조회한다.
 * 그룹 수업에서 특정 수강생의 일지를 가져올 때 사용.
 *
 * studiomateMemberId 기반 쿼리를 우선하고, 결과가 없으면 memberId fallback 쿼리를 수행한다.
 */
export async function getJournalByClassAndMember(
  ownerUid: string,
  centerId: number,
  classId: string,
  memberId: string,
): Promise<Journal | null> {
  // 1차: studiomateMemberId 기반 쿼리
  const memberIdNum = Number(memberId);
  if (!isNaN(memberIdNum)) {
    const q = query(
      journalsCollection(ownerUid, centerId),
      where("classId", "==", classId),
      where("studiomateMemberId", "==", memberIdNum),
    );
    const snap = await getDocs(q);
    if (!snap.empty) {
      const docSnap = snap.docs[0];
      return {
        id: docSnap.id,
        ...(docSnap.data() as Omit<Journal, "id">),
      };
    }
  }

  // 2차: memberId fallback (구버전 호환)
  const q = query(
    journalsCollection(ownerUid, centerId),
    where("classId", "==", classId),
    where("memberId", "==", memberId),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const docSnap = snap.docs[0];
  return {
    id: docSnap.id,
    ...(docSnap.data() as Omit<Journal, "id">),
  };
}

/**
 * classId로 해당 수업의 모든 draft 일지 목록을 반환한다.
 * 그룹 수업에서 수강생별 draft 존재 여부 확인에 사용.
 */
export async function getDraftJournalsByClassId(
  ownerUid: string,
  centerId: number,
  classId: string,
): Promise<Journal[]> {
  const q = query(draftJournalsCollection(ownerUid, centerId), where("classId", "==", classId));
  const snap = await getDocs(q);
  return snap.docs.map((docSnap) => ({
    id: docSnap.id,
    ...(docSnap.data() as Omit<Journal, "id">),
  }));
}

/**
 * classId + studiomateMemberId 조합으로 특정 회원의 draft 일지를 조회한다.
 *
 * studiomateMemberId 기반 쿼리를 우선하고, 결과가 없으면 memberId fallback 쿼리를 수행한다.
 */
export async function getDraftJournalByClassAndMember(
  ownerUid: string,
  centerId: number,
  classId: string,
  memberId: string,
): Promise<Journal | null> {
  // 1차: studiomateMemberId 기반 쿼리
  const memberIdNum = Number(memberId);
  if (!isNaN(memberIdNum)) {
    const q = query(
      draftJournalsCollection(ownerUid, centerId),
      where("classId", "==", classId),
      where("studiomateMemberId", "==", memberIdNum),
    );
    const snap = await getDocs(q);
    if (!snap.empty) {
      const docSnap = snap.docs[0];
      return {
        id: docSnap.id,
        ...(docSnap.data() as Omit<Journal, "id">),
      };
    }
  }

  // 2차: memberId fallback (구버전 호환)
  const q = query(
    draftJournalsCollection(ownerUid, centerId),
    where("classId", "==", classId),
    where("memberId", "==", memberId),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const docSnap = snap.docs[0];
  return {
    id: docSnap.id,
    ...(docSnap.data() as Omit<Journal, "id">),
  };
}

/**
 * 그룹 수업 일지를 일괄 생성/수정한다 (원자적 batch write).
 *
 * @param existingJournals - 기존 일지 목록 (studiomateMemberId로 매칭하여 update/create 판별)
 * @param journalDocs - 저장할 일지 데이터 배열 (studiomateMemberId 포함)
 * @returns 저장된 journal ID 배열
 */
export async function batchSaveGroupJournals(
  ownerUid: string,
  centerId: number,
  existingJournals: Journal[],
  journalDocs: Array<Omit<JournalDocument, "createdAt" | "updatedAt">>,
): Promise<string[]> {
  if (isDemoMode()) {
    // 데모 모드: 순차적 저장
    const ids: string[] = [];
    for (const data of journalDocs) {
      const existing = existingJournals.find(
        (j) => j.studiomateMemberId === data.studiomateMemberId,
      );
      if (existing) {
        await demoStore.updateJournal(centerId, existing.id, data);
        ids.push(existing.id);
      } else {
        const id = await demoStore.saveJournal(centerId, data);
        ids.push(id);
      }
    }
    return ids;
  }

  const db = getFirebaseFirestore();
  const batch = writeBatch(db);
  const col = journalsCollection(ownerUid, centerId);
  const ids: string[] = [];

  // 기존 일지를 studiomateMemberId → journal 매핑
  const existingMap = new Map<number, Journal>();
  for (const j of existingJournals) {
    if (j.studiomateMemberId !== undefined) {
      existingMap.set(j.studiomateMemberId, j);
    }
  }

  for (const data of journalDocs) {
    if (!data.exercises || data.exercises.length === 0) {
      throw new Error("Journal must have at least one exercise");
    }

    const existing =
      data.studiomateMemberId !== undefined ? existingMap.get(data.studiomateMemberId) : undefined;

    if (existing) {
      // Update existing journal
      const ref = doc(col, existing.id);
      batch.update(ref, {
        ...data,
        updatedAt: serverTimestamp(),
      });
      ids.push(existing.id);
    } else {
      // Create new journal
      const ref = doc(col);
      batch.set(ref, {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      ids.push(ref.id);
    }
  }

  await batch.commit();
  return ids;
}
